import {selectDesignerAbility} from '@packages/systems/designer-abilities';
import {
  selectIsPreviewMode,
  selectSiteShortName,
} from '@packages/systems/designer-selectors';
import {useSiteOptimizationPageMappings} from '../queries/useSiteOptimizationMappings';
import {UseDesignerSelector} from '../_internal-shared-types';
import {ExperienceOptimizationJson} from '@packages/systems/optimization/types';

type PermissionCheck = {
  hasChanges: boolean;
  canModify: boolean;
};

type PermissionChecks = Record<string, PermissionCheck>;

const canModifyComponentsInOptimizationSelector = selectDesignerAbility(
  'canModifyComponentsInOptimization'
);
const canModifyStyleBlocksInOptimizationSelector = selectDesignerAbility(
  'canModifyStyleBlocksInOptimization'
);

const canModifyComponentInstanceInOptimizationSelector = selectDesignerAbility(
  'canModifyComponentInstanceInOptimization'
);

export const _useIsOptimizationWrapperActionAllowed = (
  useDesignerSelector: UseDesignerSelector
) => {
  return function useIsOptimizationWrapperActionAllowed(
    experience: ExperienceOptimizationJson | null
  ) {
    const siteShortName = useDesignerSelector(selectSiteShortName);
    const {data: optimizationMappings} =
      useSiteOptimizationPageMappings(siteShortName);
    const canModifyComponentsInOptimization = useDesignerSelector(
      canModifyComponentsInOptimizationSelector
    );
    const canModifyStyleBlocksInOptimization = useDesignerSelector(
      canModifyStyleBlocksInOptimizationSelector
    );
    const canModifyComponentInstanceInOptimization = useDesignerSelector(
      canModifyComponentInstanceInOptimizationSelector
    );
    const isInPreviewMode = useDesignerSelector(selectIsPreviewMode);
    if (isInPreviewMode || !experience) {
      return false;
    }
    const experienceId = experience.id;
    const variationIds = new Set(
      Object.values(experience.variations).map((variation) => variation.id)
    );
    const hasSymbolChanges = optimizationMappings?.symbols
      ? Object.values(optimizationMappings.symbols).some((symbol) =>
          symbol.experienceIds.includes(experienceId)
        )
      : false;

    const hasSymbolInstanceChanges = optimizationMappings?.pages
      ? Object.values(optimizationMappings.pages).some((page) => {
          const symbolTypes =
            page.experienceIdsWithLocalSymbolUpdates?.[experienceId];
          return symbolTypes?.includes('Instance') ?? false;
        })
      : false;

    const hasStyleChanges = optimizationMappings?.styledPages
      ? optimizationMappings?.styledPages.variationIds.some((variationId) =>
          variationIds.has(variationId)
        )
      : false;

    const permissionChecks: PermissionChecks = {
      symbol: {
        hasChanges: hasSymbolChanges,
        canModify: canModifyComponentsInOptimization,
      },
      symbolInstance: {
        hasChanges: hasSymbolInstanceChanges,
        canModify: canModifyComponentInstanceInOptimization,
      },
      style: {
        hasChanges: hasStyleChanges,
        canModify: canModifyStyleBlocksInOptimization,
      },
    };

    return Object.values(permissionChecks).every(
      ({hasChanges, canModify}) => !hasChanges || canModify
    );
  };
};
